import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { navigate } from 'gatsby'
import { Backdrop, CircularProgress, useTheme } from '@mui/material'
import { useCsrf } from './CsrfContext'

const AuthContext = createContext({})
export const useAuth = () => useContext(AuthContext)
const isBrowser = typeof window !== 'undefined'
const LOGOUT_GUARD_KEY = 'auth-signout-state'
const AUTH_HINT_COOKIE_PREFIX = 'auth_hint='

const hasCookieWithPrefix = (prefix) => {
  if (!isBrowser || typeof document.cookie !== 'string' || !prefix) return false
  return document.cookie.split(';').some((segment) => {
    const trimmed = segment.trim()
    return trimmed.startsWith(prefix) && trimmed.length > prefix.length
  })
}

const hasAuthHintCookie = () => hasCookieWithPrefix(AUTH_HINT_COOKIE_PREFIX)

const getBackdropSx = (t) => ({ color: '#fff', zIndex: t.zIndex.modal + 1, backgroundColor: 'rgba(9,42,72,.4)' })

function Loading() {
  const t = useTheme()
  return <Backdrop open sx={getBackdropSx(t)}><CircularProgress size={48} thickness={4} /></Backdrop>
}

const parseBody = async (response) => {
  const text = await response.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const readLogoutGuard = () => {
  if (!isBrowser) return null
  const raw = sessionStorage.getItem(LOGOUT_GUARD_KEY)
  if (!raw) return null
  try {
    const payload = JSON.parse(raw)
    if (payload && typeof payload === 'object') return payload
  } catch {
    /* ignore */
  }
  return null
}

const writeLogoutGuard = (state) => {
  if (!isBrowser) return
  if (!state) {
    sessionStorage.removeItem(LOGOUT_GUARD_KEY)
    return
  }
  sessionStorage.setItem(LOGOUT_GUARD_KEY, JSON.stringify({ state, ts: Date.now() }))
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null)
  const [loadingCount, setLoadingCount] = useState(0)
  const [hydrated, setHydrated] = useState(false)
  const loading = loadingCount > 0
  const { ensure } = useCsrf()

  const beginLoading = useCallback(() => setLoadingCount((count) => count + 1), [])
  const endLoading = useCallback(() => setLoadingCount((count) => (count > 0 ? count - 1 : 0)), [])

  const refreshRef = useRef(null)
  const syncRef = useRef(() => Promise.resolve(null))
  const refreshing = useRef(null)
  const refreshTimeout = useRef(null)
  const hasRefresh = useRef(true)
  const verifyPending = useRef(null)
  const lastRefresh = useRef(0)
  const lastVerifyAt = useRef(0)
  const verifyRef = useRef(() => Promise.resolve(null))

  const applyUser = useCallback((nextUser) => {
    setUserState(nextUser || null)
    if (!nextUser && refreshTimeout.current) {
      clearTimeout(refreshTimeout.current)
      refreshTimeout.current = null
    }
    if (nextUser && isBrowser) writeLogoutGuard(null)
  }, [])

  const api = useCallback(async (path, { method = 'GET', body, csrf = false, retry = true, signal } = {}) => {
    const headers = { 'Content-Type': 'application/json' }
    if (csrf && method !== 'GET') {
      headers['x-csrf-token'] = await ensure()
    }

    const makeRequest = async () => {
      const response = await fetch(`${path}`, {
        method,
        credentials: 'include',
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal
      })
      const payload = await parseBody(response)
      if (!response.ok) {
        const detail = typeof payload === 'object' && payload !== null ? (payload.error || payload.message) : null
        const error = new Error(detail || response.statusText || 'Request failed')
        error.status = response.status
        error.body = payload
        throw error
      }
      return typeof payload === 'object' && payload !== null ? payload : {}
    }

    try {
      return await makeRequest()
    } catch (err) {
      if (retry && err?.status === 401 && err?.body && (err.body.error === 'Bad CSRF' || err.body.message === 'Bad CSRF')) {
        await ensure(true)
        return makeRequest()
      }
      throw err
    }
  }, [ensure])

  const scheduleRefresh = useCallback((u) => {
    if (!u) return
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current)
    const expSec = Number(u?.exp)
    if (!Number.isFinite(expSec)) return
    const targetTime = expSec * 1000
    const skew = 120000
    const jitter = Math.floor(Math.random() * 30000)
    let delay = targetTime - Date.now() - skew - jitter
    if (delay < 10000) delay = 10000
    refreshTimeout.current = setTimeout(() => refreshRef.current?.(), delay)
  }, [])

  const clearSession = useCallback(() => {
    hasRefresh.current = false
    applyUser(null)
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current)
      refreshTimeout.current = null
    }
    if (isBrowser) document.cookie = 'auth_hint=; Max-Age=0; path=/'
  }, [applyUser])

  const redirectToLogin = useCallback(() => {
    if (!isBrowser) return
    const currentPath = window.location.pathname || '/'
    const currentSearch = window.location.search || ''
    const redirectTarget = currentPath.includes('/logout') ? '/' : `${currentPath}${currentSearch}`
    navigate(`/institutional/login/?redirect=${encodeURIComponent(redirectTarget)}`)
  }, [])

  const invokeVerifySilently = useCallback(() => {
    try {
      const maybe = verifyRef.current?.()
      if (maybe && typeof maybe.catch === 'function') {
        maybe.catch(() => null)
      }
    } catch (verifyError) {
      console.warn('verify() invocation failed after refresh error', verifyError)
    }
  }, [])

  const refresh = useCallback(() => {
    if (!hasRefresh.current) return Promise.resolve(null)
    if (refreshing.current) return refreshing.current

    refreshing.current = api('/api/cognito-refresh', { method: 'POST', csrf: true })
      .then((data) => {
        if (!data || typeof data !== 'object') return null
        applyUser(data)
        hasRefresh.current = true
        scheduleRefresh(data)
        lastRefresh.current = Date.now()
        return data
      })
      .catch((err) => {
        const rawError = err?.body?.error
        const normalizedError = typeof rawError === 'string' ? rawError.toLowerCase() : ''

        if (normalizedError.includes('invalid_grant') || normalizedError.includes('refresh token')) {
          hasRefresh.current = false
          console.warn('Refresh token rejected; falling back to verify()', err)
          invokeVerifySilently()
          return null
        }

        if (normalizedError.includes('missing refresh')) {
          hasRefresh.current = false
          console.warn('Refresh token unavailable; falling back to verify()', err)
          invokeVerifySilently()
          return null
        }

        if (normalizedError.includes('bad csrf')) {
          console.warn('Refresh blocked by CSRF mismatch; re-syncing session', err)
          invokeVerifySilently()
          return null
        }

        if (err?.status === 401) {
          clearSession()
          return null
        }
        console.warn('Refresh failed', err)
        return null
      })
      .finally(() => {
        refreshing.current = null
      })

    return refreshing.current
  }, [api, applyUser, clearSession, invokeVerifySilently, scheduleRefresh])

  refreshRef.current = refresh

  const VERIFY_COOLDOWN_MS = 5000

  const verify = useCallback(async () => {
    if (verifyPending.current) return verifyPending.current
    const now = Date.now()
    if (now - lastVerifyAt.current < VERIFY_COOLDOWN_MS) return user
    verifyPending.current = (async () => {
      lastVerifyAt.current = Date.now()
      try {
        const verified = await api('/api/cognito-verify', { retry: false })
        if (verified && typeof verified === 'object' && !verified.error) {
          applyUser(verified)
          hasRefresh.current = true
          scheduleRefresh(verified)
          return verified
        }
        applyUser(null)
        return null
      } catch (err) {
        if (err?.status === 401) {
          applyUser(null)
          return null
        }
        console.warn('Session verification failed', err)
        return user
      }
    })()
    try {
      return await verifyPending.current
    } finally {
      verifyPending.current = null
    }
  }, [api, applyUser, scheduleRefresh, user])

  verifyRef.current = verify

  const sync = useCallback(async () => {
    const refreshed = await refresh()
    if (refreshed && typeof refreshed === 'object' && !refreshed.error) return refreshed
    return verify()
  }, [refresh, verify])

  syncRef.current = sync

  const login = useCallback(async (email, password) => {
    beginLoading()
    try {
      await api('/api/cognito-login', { method: 'POST', body: { username: email, password }, csrf: true })
      const u = await sync()
      if (!u) throw new Error('Unable to verify login session')
      return u
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading, sync])

  const oAuthLogin = useCallback(async (sub, code) => {
    beginLoading()
    try {
      const body = { code }
      if (sub) body.user_sub = sub
      await api('/api/cognito-oauth-login', { method: 'POST', body, csrf: true })
      const u = await sync()
      if (!u) throw new Error('Unable to complete OAuth login')
      return u
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading, sync])

  const confirmEmail = useCallback(async (email, id, domainState) => {
    beginLoading()
    try {
      await api('/api/cognito-confirm-email', { method: 'POST', body: { business_email: email, user_id: id, domain: domainState }, csrf: true })
      return await sync()
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading, sync])

  const confirmEmailNoSync = useCallback(async (email, id, domainState) => {
    beginLoading()
    try {
      return await api('/api/cognito-confirm-email', { method: 'POST', body: { business_email: email, user_id: id, domain: domainState }, csrf: true })
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading])

  const confirmCode = useCallback(async (id, token) => {
    beginLoading()
    try {
      const payload = await api('/api/cognito-confirm-code', { method: 'POST', body: { user_id: id, token }, csrf: true, retry: false })
      const refreshedUser = await sync()
      return { user: refreshedUser, payload }
    } finally {
      endLoading()
    }
  }, [api, beginLoading, endLoading, sync])

  const loggingOut = useRef(null)

  const logout = useCallback(() => {
    const guard = readLogoutGuard()
    const now = Date.now()
    if (guard?.state === 'pending') return loggingOut.current || Promise.resolve(null)
    if (guard?.state === 'done' && typeof guard.ts === 'number' && now - guard.ts < 5000) {
      return loggingOut.current || Promise.resolve(null)
    }

    if (loggingOut.current) return loggingOut.current
    writeLogoutGuard('pending')

    loggingOut.current = (async () => {
      beginLoading()
      try {
        await api('/api/cognito-signout', { method: 'POST', csrf: true })
      } catch (err) {
        console.warn('Sign out request failed', err)
      } finally {
        endLoading()
        clearSession()
        writeLogoutGuard('done')
        redirectToLogin()
      }
    })().finally(() => {
      loggingOut.current = null
    })
    return loggingOut.current
  }, [api, beginLoading, endLoading, clearSession, redirectToLogin])

  const signup = useCallback((e, g, f, req, d) => api('/api/cognito-signup', { method: 'POST', body: { email: e, given_name: g, family_name: f, request_institutional_access: req, domain_status: d }, csrf: true }), [api])

  const signupReset = useCallback((code, sub, pw, d, email) => {
    const body = { code, userSub: sub, password: pw, domain_status: d }
    const normalizedEmail = typeof email === 'string' ? email.trim() : ''
    if (normalizedEmail) body.email = normalizedEmail
    return api('/api/cognito-signup-reset', { method: 'POST', body, csrf: true })
  }, [api])

  const forgotPasswordInit = useCallback((email) => api('/api/cognito-forgot-password-init', { method: 'POST', body: { email }, csrf: true }), [api])

  const forgotPassword = useCallback((code, sub, pw) => api('/api/cognito-forgot-password', { method: 'POST', body: { code, userSub: sub, password: pw }, csrf: true }), [api])

  const requestReset = useCallback((email) => forgotPasswordInit(email), [forgotPasswordInit])
  const confirmReset = useCallback((email, code, password) => forgotPassword(code, email, password), [forgotPassword])

  useEffect(() => {
    let cancelled = false
    const hydrate = async () => {
      try {
        const hintAvailable = hasAuthHintCookie()
        const result = await verifyRef.current?.()
        if (!result && hintAvailable) {
          await syncRef.current?.()
        }
      } finally {
        if (!cancelled) setHydrated(true)
      }
    }
    hydrate()
    return () => {
      cancelled = true
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current)
        refreshTimeout.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isBrowser) return undefined
    const handler = () => {
      if (document.visibilityState !== 'visible') return
      const remaining = user ? user.exp * 1000 - Date.now() : 0
      if (remaining < 300000 && Date.now() - lastRefresh.current > 30000) {
        refreshRef.current?.()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [user])

  const access = useMemo(() => ({
    groups: user?.groups || [],
    isInstitutional: user?.groups?.includes('institutional_access') || false
  }), [user])

  const ctx = useMemo(() => ({
    user,
    access,
    loading,
    hydrated,
    confirmEmail,
    confirmEmailNoSync,
    confirmCode,
    login,
    oAuthLogin,
    logout,
    signup,
    signupReset,
    forgotPasswordInit,
    forgotPassword,
    requestReset,
    confirmReset,
    verify,
    refresh,
    getUserFresh: sync,
    sync
  }), [user, access, loading, hydrated, confirmEmail, confirmEmailNoSync, confirmCode, login, oAuthLogin, logout, signup, signupReset, forgotPasswordInit, forgotPassword, requestReset, confirmReset, verify, refresh, sync])

  if (!hydrated) return isBrowser ? <Loading /> : null

  return (
    <AuthContext.Provider value={ctx}>
      {children}
      {loading ? <Loading /> : null}
    </AuthContext.Provider>
  )
}
